import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/NewButton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FolderPlus } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { createFolder, FolderCreate, Folder } from '@/hooks/useFolders';
import { apiErrorMessage } from '@/lib/api-client';
import { useMatterLabels } from '@/lib/useMatterLabels';
import { newFolderNameError } from '@/utils/document-utils';

interface CreateFolderDialogProps {
  matterSlug: string;
  parentFolder?: Folder;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const CreateFolderDialog: React.FC<CreateFolderDialogProps> = ({
  matterSlug,
  parentFolder,
  isOpen,
  onClose,
  onSuccess
}) => {
  const label = useMatterLabels();
  const [folderName, setFolderName] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleCreate = async () => {
    const validationError = newFolderNameError(folderName, parentFolder);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    setIsLoading(true);
    try {
      const folderData: FolderCreate = {
        name: folderName.trim(),
        parent_folder_id: parentFolder?.folder_id
      };

      await createFolder(matterSlug, folderData);
      toast.success('Folder created successfully');
      setFolderName('');
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error creating folder:', error);
      const legacyError = error?.response?.data?.error;
      toast.error(apiErrorMessage(error) || (typeof legacyError === 'string' ? legacyError : null) || 'Failed to create folder');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setFolderName('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderPlus className="h-5 w-5 text-amber-500/40 fill-amber-400/15" />
            Create new folder
          </DialogTitle>
          <DialogDescription>
            {parentFolder
              ? `Create a new subfolder within "${parentFolder.name}"`
              : label('organise-folder-hint')
            }
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">

          <div>
            <Label htmlFor="folder-name" className="block pb-2">Folder name</Label>
            <Input
              id="folder-name"
              placeholder="Enter folder name"
              value={folderName}
              onChange={(e) => setFolderName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleCreate();
                }
              }}
              disabled={isLoading}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isLoading} text='Cancel' />
          <Button onClick={handleCreate} disabled={isLoading || !folderName.trim()} loading={isLoading} text='Create folder' />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
